/**
 * 音频资源国际化工具
 * 根据当前语言环境加载对应的音频文件
 */

import { mapLocale } from '@/types/locale'

// 音效资源懒加载表: key 为源码相对路径, value 为按需解析 URL 的动态 import()。
// 之前用 `new URL(动态路径, import.meta.url)` 写法, Vite 会把它编译成"隐式 eager glob"——
// 只要 locale-audio.ts 被 import (App.vue 的 useBgMusic() 就是无条件调用), dev 模式下
// 会立刻把 src/assets/sounds 下 4 个语言的全部 mp3 当成模块请求一遍 (与是否真的要播放音效无关);
// 生产环境虽不会真的发网络请求, 但会把 130+ 个文件路径的查找表打进引用它的 chunk。
// 改成 eager:false 后, 每个文件只有真正被 getLocaleAudio() 调用到时才会触发它自己的动态 import()。
const audioModules = import.meta.glob<string>('../assets/sounds/*/*.mp3', { import: 'default' })

/**
 * 获取国际化音频资源路径 (按需加载, 不会预先拉取所有语言的音频文件)
 *
 * @param filename - 音频文件名 (例如: 'banker_win.mp3')
 * @param locale - 语言代码 (zh/en/ko/vi 或 zh-CN/en-US 等)
 * @returns 音频文件的完整 URL
 *
 * @example
 * ```typescript
 * const audioUrl = await getLocaleAudio('banker_win.mp3', 'zh')
 * ```
 */
export async function getLocaleAudio(filename: string, locale: string): Promise<string> {
  // 映射语言代码到资源目录名
  const mappedLocale = mapLocale(locale)

  const key = `../assets/sounds/${mappedLocale}/${filename}`
  const fallbackKey = `../assets/sounds/zh/${filename}`
  const loader = audioModules[key] ?? audioModules[fallbackKey]

  if (!loader) {
    console.error(`[LocaleAudio] 找不到音频文件: ${filename} (locale: ${locale})`)
    return ''
  }

  try {
    return await loader()
  } catch (error) {
    console.error(`[LocaleAudio] 加载音频失败: ${filename} (locale: ${locale})`, error)
    // 失败时尝试返回中文版本作为降级
    const fallback = audioModules[fallbackKey]
    return fallback ? await fallback() : ''
  }
}

/**
 * 预加载音频文件
 * 用于在游戏开始前预加载常用音效,减少播放延迟
 *
 * @param filenames - 音频文件名列表
 * @param locale - 语言代码
 * @returns Promise<Audio[]> 预加载的 Audio 对象数组
 *
 * @example
 * ```typescript
 * const audios = await preloadAudios(['banker_win.mp3', 'player_win.mp3'], 'zh')
 * ```
 */
export async function preloadAudios(filenames: string[], locale: string): Promise<HTMLAudioElement[]> {
  const promises = filenames.map(async filename => {
    const url = await getLocaleAudio(filename, locale)
    return new Promise<HTMLAudioElement>((resolve, reject) => {
      const audio = new Audio(url)
      audio.preload = 'auto'
      audio.addEventListener('canplaythrough', () => resolve(audio), { once: true })
      audio.addEventListener('error', reject, { once: true })
      audio.load()
    })
  })

  try {
    return await Promise.all(promises)
  } catch (error) {
    console.error('[LocaleAudio] 预加载音频失败:', error)
    return []
  }
}

/**
 * 常用音效文件名常量
 */
export const SOUND_FILES = {
  // ========== 百家乐音效 ==========
  BJL: {
    BANKER_WIN: 'banker_win.mp3', // 庄赢
    PLAYER_WIN: 'player_win.mp3', // 闲赢
    TIE: 'tie_win.mp3', // 和
    BPAIR_BANKER_WIN: 'bpair_banker_win.mp3', // 庄对+庄赢
    BPAIR_PLAYER_WIN: 'bpair_player_win.mp3', // 庄对+闲赢
    BPAIR_TIE_WIN: 'bpair_tie_win.mp3', // 庄对+和
    PPAIR_BANKER_WIN: 'ppair_banker_win.mp3', // 闲对+庄赢
    PPAIR_PLAYER_WIN: 'ppair_player_win.mp3', // 闲对+闲赢
    PPAIR_TIE_WIN: 'ppair_tie_win.mp3', // 闲对+和
    BPPAIR_BANKER_WIN: 'bppair_banker_win.mp3', // 庄闲对+庄赢
    BPPAIR_PLAYER_WIN: 'bppair_player_win.mp3', // 庄闲对+闲赢
    BPPAIR_TIE_WIN: 'bppair_tie_win.mp3', // 庄闲对+和
  },

  // ========== 龙虎斗音效 ==========
  LHD: {
    LOONG_WIN: 'loong_win.mp3', // 龙赢
    TIGER_WIN: 'tiger_win.mp3', // 虎赢
    TIE: 'tie_win.mp3', // 和
  },

  // ========== 牛牛音效 ==========
  BULL: {
    BANKER: 'bull_banker.mp3', // 庄
    BANKER_WIN: 'bull_banker_win.mp3', // 庄赢
    PLAYER1_WIN: 'bull_player1_win.mp3', // 闲1赢
    PLAYER2_WIN: 'bull_player2_win.mp3', // 闲2赢
    PLAYER3_WIN: 'bull_player3_win.mp3', // 闲3赢
    PLAYER12_WIN: 'bull_player12_win.mp3', // 闲1+闲2赢
    PLAYER23_WIN: 'bull_player23_win.mp3', // 闲2+闲3赢
    PLAYER13_WIN: 'bull_player13_win.mp3', // 闲1+闲3赢
    PLAYER123_WIN: 'bull_player123_win.mp3', // 闲1+闲2+闲3赢
    BULL1: 'bull_bull1.mp3', // 牛1
    BULL2: 'bull_bull2.mp3', // 牛2
    BULL3: 'bull_bull3.mp3', // 牛3
    BULL4: 'bull_bull4.mp3', // 牛4
    BULL5: 'bull_bull5.mp3', // 牛5
    BULL6: 'bull_bull6.mp3', // 牛6
    BULL7: 'bull_bull7.mp3', // 牛7
    BULL8: 'bull_bull8.mp3', // 牛8
    BULL9: 'bull_bull9.mp3', // 牛9
    BULL_BULL: 'bull_bullbull.mp3', // 牛牛
    NO_BULL: 'bull_no_bull.mp3', // 无牛
  },

  // ========== 杆子宝音效 ==========
  // 结果组合: 31(干), 32/33(通+1对), 32/34(通+花对)
  // 通(32)总是伴随对子出现,没有单独音效
  GZB: {
    DIFFERENT_WIN: 'different_win.mp3', // 干 (31)
    PAIR_ONE: 'same_win_two_one.mp3', // 1对 (33)
    PAIR_FLOWER: 'same_win_two_flowers.mp3', // 花对 (34)
  },

  // ========== 背景音乐 ==========
  BGM: {
    GAME_BG1: 'game_bg1.mp3',
    GAME_BG2: 'game_bg2.mp3',
    GAME_BG3: 'game_bg3.mp3',
  },

  // ========== 通用音效 ==========
  COMMON: {
    TEN_SECOND: 'ten_second.mp3', // 10秒倒计时
    PLEASE_BET: 'start_wager.mp3', // 请下注
    STOP_BET: 'end_wager.mp3', // 停止下注
  },
} as const
